import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Card from '../../../components/ui/Card';
import { generateDashboardInsights } from '../../../services/aiAnalytics';
import toast from 'react-hot-toast';

const AIInsightsCard = ({ metrics }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (error) {
      toast?.error(error?.message || 'Failed to generate AI insights');
    }
  }, [error]);

  useEffect(() => {
    if (metrics) {
      loadInsights();
    }
  }, [metrics]);

  const loadInsights = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await generateDashboardInsights(metrics);
      setInsights(result);
    } catch (err) {
      console.error('AI insights error:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const getHealthColor = (score) => {
    if (score >= 80) return 'text-success-600';
    if (score >= 60) return 'text-warning-600';
    return 'text-danger-600';
  };

  const getHealthBgColor = (score) => {
    if (score >= 80) return 'bg-success-50 border-success-200';
    if (score >= 60) return 'bg-warning-50 border-warning-200';
    return 'bg-danger-50 border-danger-200';
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Icon name="Sparkles" className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">AI Business Insights</h3>
            <p className="text-sm text-muted-foreground">Powered by OpenAI</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </Card>
    );
  }

  if (!insights) {
    return null;
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Icon name="Sparkles" className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">AI Business Insights</h3>
            <p className="text-sm text-muted-foreground">Powered by OpenAI</p>
          </div>
        </div>
        <button
          onClick={loadInsights}
          disabled={loading}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
          title="Refresh insights"
        >
          <Icon name="RefreshCw" className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      {/* Health Score */}
      <div className={`p-4 rounded-lg border mb-4 ${getHealthBgColor(insights?.healthScore)}`}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-foreground">Overall Health Score</span>
          <span className={`text-2xl font-bold ${getHealthColor(insights?.healthScore)}`}>
            {insights?.healthScore}/100
          </span>
        </div>
      </div>

      {/* Performance Summary */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-foreground mb-2">Performance Summary</h4>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {insights?.summary}
        </p>
      </div>

      {/* Top Priorities */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
          <Icon name="Target" className="w-4 h-4" />
          Top Priorities
        </h4>
        <ul className="space-y-2">
          {insights?.priorities?.map((priority, index) => (
            <li key={index} className="flex items-start gap-2 text-sm text-foreground">
              <span className="text-primary-600 font-semibold mt-0.5">{index + 1}.</span>
              <span>{priority}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Urgent Actions */}
      {insights?.urgentActions?.length > 0 && (
        <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-warning-800 mb-2 flex items-center gap-2">
            <Icon name="AlertTriangle" className="w-4 h-4" />
            Urgent Actions Required
          </h4>
          <ul className="space-y-1.5">
            {insights?.urgentActions?.map((action, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-warning-700">
                <Icon name="ArrowRight" className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{action}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
};

export default AIInsightsCard;